<!--
  CriticalBanner.vue — Phase 2.3: Full-screen CRITICAL alert overlay.
  
  Props: 
    alerts - Array of critical notifications
  
  Cannot be dismissed without an action (Acknowledge or Escalate).
  Shows red pulse overlay + event details + action buttons.
-->
<script setup>
import { computed } from 'vue'
import StatusBadge from './StatusBadge.vue'

const AGENTS = {
  centinela:  { label: 'El Centinela', icon: 'mdi-shield-lock-outline', color: 'agent-centinela' },
  jefe:       { label: 'Jefe de Tráfico', icon: 'mdi-truck-fast-outline', color: 'agent-jefe' },
  supervisor: { label: 'El Supervisor', icon: 'mdi-hard-hat', color: 'agent-supervisor' },
  ingeniero:  { label: 'El Ingeniero', icon: 'mdi-office-building-cog-outline', color: 'agent-ingeniero' },
  system:     { label: 'System', icon: 'mdi-cog-outline', color: 'grey' },
}

const props = defineProps({
  /** Array of critical alert objects */
  alerts: { type: Array, default: () => [] },
  /** Whether audio is muted */
  muted: { type: Boolean, default: false },
})

const emit = defineEmits(['acknowledge', 'escalate', 'toggle-mute'])

const show = computed(() => props.alerts.length > 0)
const current = computed(() => props.alerts[0] || null)
const currentAgent = computed(() => AGENTS[current.value?.agent] || AGENTS.system)
const remaining = computed(() => Math.max(0, props.alerts.length - 1))

function formatTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function handleAcknowledge() {
  if (current.value) {
    emit('acknowledge', current.value.id)
  }
}

function handleEscalate() {
  if (current.value) {
    emit('escalate', current.value.id)
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="critical-banner">
      <div
        v-if="show && current"
        class="critical-banner-overlay"
      >
        <!-- Pulsing background -->
        <div class="critical-pulse-bg" />

        <!-- Content -->
        <div class="critical-banner-content">
          <!-- Header -->
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="d-flex align-center ga-3">
              <VIcon icon="mdi-alert-octagon" color="white" size="32" />
              <span class="text-h5 text-white font-weight-bold">CRITICAL ALERT</span>
            </div>
            <div class="d-flex align-center ga-2">
              <VBtn
                :icon="muted ? 'mdi-volume-off' : 'mdi-volume-high'"
                variant="text"
                color="white"
                size="small"
                @click="$emit('toggle-mute')"
              />
              <VChip v-if="remaining > 0" color="error" variant="flat" size="small">
                +{{ remaining }} more
              </VChip>
            </div>
          </div>

          <!-- Alert Card -->
          <VCard
            class="critical-alert-card mx-auto"
            max-width="600"
            elevation="24"
          >
            <VCardItem>
              <template #prepend>
                <VAvatar :color="currentAgent.color" size="48" variant="tonal">
                  <VIcon :icon="currentAgent.icon" size="24" />
                </VAvatar>
              </template>

              <VCardTitle class="text-h6">
                {{ currentAgent.label }}
              </VCardTitle>
              <VCardSubtitle>
                {{ formatTime(current.timestamp) }}
              </VCardSubtitle>

              <template #append>
                <StatusBadge severity="critical" variant="chip" pulse />
              </template>
            </VCardItem>

            <VDivider />

            <VCardText>
              <div class="text-h6 mb-2">{{ current.title }}</div>
              <div v-if="current.narrative" class="text-body-1 text-medium-emphasis mb-3">
                {{ current.narrative }}
              </div>
              <div v-if="current.source" class="text-caption text-medium-emphasis">
                Source: {{ current.source }}
              </div>
            </VCardText>

            <VDivider />

            <VCardActions class="pa-4">
              <VSpacer />
              <VBtn
                color="warning"
                variant="outlined"
                size="large"
                prepend-icon="mdi-arrow-up-bold"
                @click="handleEscalate"
              >
                Escalate
              </VBtn>
              <VBtn
                color="success"
                variant="flat"
                size="large"
                prepend-icon="mdi-check-circle-outline"
                @click="handleAcknowledge"
              >
                Acknowledge
              </VBtn>
            </VCardActions>
          </VCard>

          <div class="text-center mt-4 text-white text-caption">
            This alert requires an action before it can be dismissed.
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.critical-banner-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(127, 29, 29, 0.92);
  backdrop-filter: blur(8px);
}

.critical-pulse-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(239, 68, 68, 0.3) 0%, transparent 70%);
  animation: critical-pulse 2s ease-in-out infinite;
  pointer-events: none;
}

@keyframes critical-pulse {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
}

.critical-banner-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 700px;
  padding: 2rem;
}

.critical-alert-card {
  border: 2px solid rgb(var(--v-theme-error));
}

/* Transition */
.critical-banner-enter-active {
  transition: all 0.3s ease-out;
}
.critical-banner-leave-active {
  transition: all 0.2s ease-in;
}
.critical-banner-enter-from {
  opacity: 0;
  transform: scale(1.05);
}
.critical-banner-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
